import React from 'react';
import Modal from 'react-modal';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, MapPin, Building } from 'lucide-react';
import { Badge } from './ui/badge';

// Custom arrow components for the carousel
const NextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full z-10 transition-all duration-300"
  >
    <ChevronRight className="w-6 h-6" />
  </button>
);

const PrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full z-10 transition-all duration-300"
  >
    <ChevronLeft className="w-6 h-6" />
  </button>
);

const ProjectModal = ({ isOpen, onClose, project }) => {
    const { t } = useTranslation();

    if (!isOpen || !project) {
      return null;
    }

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dotsClass: 'slick-dots custom-dots',
    customPaging: (i) => (
      <div className="w-3 h-3 bg-white/50 hover:bg-white rounded-full transition-all duration-300"></div>
    ),
    appendDots: (dots) => (
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <ul className="flex space-x-2 justify-center">{dots}</ul>
      </div>
    )
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-content"
      overlayClassName="modal-overlay"
      contentLabel="Project Details"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="flex flex-col h-full max-h-[90vh]">
        {/* Image Carousel */}
        <div className="relative w-full h-64">
          <Slider {...sliderSettings}>
            {project.images && project.images.map((image, index) => (
              <div key={index} className="h-64">
                <img
                  src={image}
                  alt={`${t(project.title)} - Image ${index + 1}`}
                  className="w-full h-full object-contain"
                  onError={(e) => e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg?20200913095930'}
                />
              </div>
            ))}
          </Slider>
        </div>

        {/* Project Details */}
        <div className="w-full p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge
                  variant="secondary"
                  className="bg-red-600 text-white border-0 font-medium"
                >
                  {project.type && t(`projects.filters.${project.type}`)}
                </Badge>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                {project.title && t(project.title)}
              </h2>
              <div className="flex items-center text-gray-400 mb-4">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{project.location && t(project.location)}</span>
              </div>
            </div>

            {/* Description */}
             <div>
               <p className="text-gray-300 leading-relaxed">
                 {project.description && t(project.description)}
               </p>
             </div>

            {/* Project Stats */}
             <div className="grid grid-cols-1 gap-4">
               <div className="text-center p-4 bg-gray-700/50 rounded-lg">
                 <Building className="w-6 h-6 text-red-500 mx-auto mb-2" />
                 <div className="text-red-500 font-bold text-lg">{project.type && t(`projects.filters.${project.type}`)}</div>
                 <div className="text-xs text-gray-400">{t('projects.details.type')}</div>
               </div>
             </div>

          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProjectModal;